import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

/**
 * Shared time-range hook.
 *
 * The workspace shell declares `from` and `to` as search params. Every canvas
 * and feed reads them via this hook so the time window is the same everywhere
 * — change the slider once and timeline, signals, map, and risk matrix all
 * respect it.
 *
 * Values are ISO date strings (YYYY-MM-DD). Empty string or undefined means
 * unbounded on that side.
 */
export function useTimeRange() {
  const navigate = useNavigate();
  // The shell defines these; child routes can read them strict:false.
  const search = useSearch({ strict: false }) as { from?: string; to?: string };

  const fromMs = useMemo(
    () => (search.from ? new Date(search.from).getTime() : null),
    [search.from],
  );
  const toMs = useMemo(
    () =>
      search.to
        ? // End-of-day for an inclusive upper bound
          new Date(search.to).getTime() + 86400_000 - 1
        : null,
    [search.to],
  );

  const set = useCallback(
    (from: string | null, to: string | null) => {
      navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          from: from || undefined,
          to: to || undefined,
        }),
      });
    },
    [navigate],
  );

  const clear = useCallback(() => set(null, null), [set]);

  const inRange = useCallback(
    (iso: string | Date | number) => {
      const t = typeof iso === "number" ? iso : new Date(iso).getTime();
      if (fromMs !== null && t < fromMs) return false;
      if (toMs !== null && t > toMs) return false;
      return true;
    },
    [fromMs, toMs],
  );

  return {
    from: search.from ?? null,
    to: search.to ?? null,
    fromMs,
    toMs,
    set,
    clear,
    inRange,
    isActive: fromMs !== null || toMs !== null,
  };
}

/**
 * Convert a duration shortcut to ISO YYYY-MM-DD pair ending today.
 * Used by the picker chips.
 */
export function lastNDays(n: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - (n - 1) * 86400_000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}
